/* eslint-disable @typescript-eslint/no-floating-promises */
import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts from 'src/lib/bitmapFonts';
import {
  animDuration,
  createWaiter,
  getCreativeSuffix,
  getScreenTop,
  parseAmount,
} from '../../lib/utils';
import Animator from 'src/lib/Animator';
import { createEmitter, createPersistentEmitter } from 'src/lib/Emitter';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import uiConfig from 'src/lib/ui/config';
import { shouldShowHeader } from 'src/redux/getters/ui';
import { Inventory } from 'src/replicant/buruburu/inventory';
import {
  ImageCoverView,
  ImageScaleView,
  ImageView,
} from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Pet } from 'src/replicant/buruburu/pet';
import StateObserver from 'src/StateObserver';
import statePromise from '../../lib/statePromise';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import { State } from 'src/state';
import { formatDuration } from 'src/lib/formatDuration';
import { Minigame } from 'src/replicant/minigame';
import { duration } from 'src/replicant/utils/duration';
import { CircularProgressBar } from './shared/CircularProgressBar';
import { Level } from 'src/replicant/buruburu/level';
import platform from '@play-co/gcinstant';
import { HudMeter, HudMeterOpts } from './HudMeter';
import { setVisibleStats } from 'src/state/ui';
import { openGemsShopSequence, isGemsButtonDisabled } from '../shop/Helpers';
import { isSceneEntered } from 'src/lib/stateUtils';

export default class Header {
  private container: View;
  private petName: LangBitmapFontTextView;
  private petSpecies: LangBitmapFontTextView;
  private petAge: LangBitmapFontTextView;
  private levelLabel: LangBitmapFontTextView;
  private coins: LangBitmapFontTextView;
  private coinsIcon: ImageView;
  private gems: LangBitmapFontTextView;
  private gemsIcon: ImageScaleView;
  private energy: LangBitmapFontTextView;
  private energyIcon: ImageView;
  private loveMeter: HudMeter;
  private stomachMeter: HudMeter;
  private switchButton: ButtonScaleView;
  private availableDisplays: string[];

  private active: boolean;

  private coinsAnimator = new Animator((value) => {
    this.coins.localeText = () => parseAmount(value);
  });

  private gemsAnimator = new Animator((value) => {
    this.gems.localeText = () => parseAmount(value);
  });

  private energyAnimator = new Animator((value) => {
    this.energy.localeText = () => `${value}/${Inventory.ruleset.maxEnergy}`;
  });
  private energyRegenerationFrame: ImageScaleView;
  private energyRegeneration: LangBitmapFontTextView;
  private levelProgress: CircularProgressBar;
  private friendsNamePlate: ImageScaleView;
  private friendsNamePlateText: LangBitmapFontTextView;

  constructor(private opts: { superview: View }) {
    this.active = false;

    StateObserver.dispatch(setVisibleStats(platform.playerID));
    this.availableDisplays = [platform.playerID];

    this.container = new View({
      tag: 'header',
      zIndex: 999,
      width: uiConfig.width,
      opacity: 0,
      infinite: true,
      canHandleEvents: false,
    });

    // anchor elements
    createEmitter(this.container, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.container.updateOpts({ y: screen.top });
      },
    );

    this.createNameTag();
    this.createLoveMeter();
    this.createStomachMeter();
    this.coins = this.createCoins();
    this.createGems();
    this.energy = this.createEnergy();
    this.createSwitchButton();
    this.createFriendsRoomPlate();

    createEmitter(this.container, ({ user }) => ({
      coins: Inventory.getters(user).getPoints(),
      gems: Inventory.getters(user).getGems(),
      energy: Inventory.getters(user).getEnergy(StateObserver.now()),
    })).addListener(({ coins, gems, energy }) => {
      this.coinsAnimator.setTarget(coins);
      this.gemsAnimator.setTarget(gems);
      this.energyAnimator.setTarget(energy);
    });

    const attibutesEmitter = createEmitter(
      this.container,
      ({ pet, level, ui: { visibleStats, isPetCrateOpening } }) => ({
        name: pet[visibleStats]?.attributes.name,
        species: pet[visibleStats]?.species,
        age: pet[visibleStats]?.attributes.age ?? 0,
        love: isPetCrateOpening ? 0 : pet[visibleStats]?.attributes.love ?? 0,
        stomach: isPetCrateOpening
          ? 0
          : pet[visibleStats]?.attributes.stomach ?? 0,
        level: level[visibleStats]?.current ?? 0,
        xp: level[visibleStats]?.xp ?? 0,
        visibleStats,
      }),
    );

    // Level Meter.
    attibutesEmitter.addListener(({ level, xp, visibleStats }) => {
      this.levelLabel.localeText = () =>
        i18n('attributes.level', { level: Level.ruleset.toDisplay(level) });
      this.levelProgress.animateProgress(xp / Level.ruleset.requiredXp(level));
      this.levelLabel.color = this.getLabelColor(visibleStats);
    });

    // Top left texts.
    attibutesEmitter.addListener(({ name, species, age, visibleStats }) => {
      this.petName.localeText = () => name ?? '';
      this.petSpecies.localeText = () =>
        Pet.ruleset.evolutions[species]?.[`name_${getCreativeSuffix()}`] ?? '';
      this.petAge.localeText = () => i18n('attributes.age', { age });

      const color = this.getLabelColor(visibleStats);
      this.petName.color = color;
      this.petSpecies.color = color;
      this.petAge.color = color;
    });

    // Love and stomach meters.
    attibutesEmitter.addListener(({ love, stomach, visibleStats }) => {
      this.loveMeter.setMeterValue(love / 100);
      this.stomachMeter.setMeterValue(stomach / 100);

      const color = this.getLabelColor(visibleStats);
      this.loveMeter.updateLabelOpts({ color });
      this.stomachMeter.updateLabelOpts({ color });
    });

    createEmitter(this.container, ({ user, pet }) => ({
      session:
        pet[user.visit.visitSession?.friendId] &&
        pet[user.visit.visitSession?.friendId].attributes &&
        user.visit.visitSession,
      isInFriendsRoom: isSceneEntered('friendsLivingRoom'),
    })).addListener(({ session, isInFriendsRoom }) => {
      if (!session) {
        StateObserver.dispatch(setVisibleStats(platform.playerID));
        this.switchButton.hide();
        this.friendsNamePlate.hide();
        attibutesEmitter.force();
        return;
      }
      StateObserver.dispatch(setVisibleStats(session.friendId));
      this.availableDisplays = [platform.playerID, session.friendId];

      this.switchButton.show();
      this.switchButton.onClick = async () => {
        sounds.sfx.play('ok');

        const { availableDisplays } = this;
        const visibleStats = StateObserver.getState().ui.visibleStats;
        const nextIndex =
          (availableDisplays.indexOf(visibleStats) + 1) %
          availableDisplays.length;
        StateObserver.dispatch(setVisibleStats(availableDisplays[nextIndex]));
        attibutesEmitter.force();
      };

      this.friendsNamePlateText.localeText = () =>
        i18n('friendsVisit.livingRoom.plate', { friend: session.name });
      this.friendsNamePlate.updateOpts({
        visible: isInFriendsRoom,
      });

      attibutesEmitter.force();
    });

    createEmitter(
      this.container,
      ({ ui, user }) =>
        Pet.getters(user).getCurrentEvolution() && !ui.isPetCrateOpening,
    ).addListener((shouldShowNameTag) => {
      this.petName.style.visible = shouldShowNameTag;
      this.petSpecies.style.visible = shouldShowNameTag;
      this.petAge.style.visible = shouldShowNameTag;
    });

    createPersistentEmitter(shouldShowHeader).addListener((showHeader) => {
      if (showHeader) {
        this.fadeIn();
      } else {
        this.fadeOut();
      }
    });
  }

  private fadeIn() {
    if (this.active) return;
    this.active = true;

    this.opts.superview.addSubview(this.container);
    animate(this.container)
      .clear()
      .wait(animDuration * 4)
      .now(
        { opacity: this.container.style.opacity, x: -100 },
        0,
        animate.easeOut,
      )
      .then(
        { opacity: this.container.style.opacity, x: -100 },
        animDuration,
        animate.easeOut,
      )
      .then({ opacity: 1, x: 0 }, animDuration, animate.easeOut);
  }

  private fadeOut() {
    if (!this.active) return;
    this.active = false;

    animate(this.container)
      .clear()
      .wait(0)
      .now(
        { opacity: this.container.style.opacity, x: this.container.style.x },
        0,
        animate.easeOut,
      )
      .then(
        { opacity: this.container.style.opacity, x: this.container.style.x },
        animDuration * 0.5,
        animate.easeOut,
      )
      .then({ opacity: 0, x: -100 }, animDuration, animate.easeOut)
      .then(() => {
        this.opts.superview.removeSubview(this.container);
      });
  }

  private getLabelColor(id: string) {
    return id === platform.playerID ? '#AB9EA7' : '#ea6c83';
  }

  // =======================================================
  // Coins
  // =======================================================

  private createCoins(): LangBitmapFontTextView {
    const layout = new ButtonScaleView({
      tag: 'coinLayout',
      superview: this.container,
      ...uiConfig.hud.coinLayout,
      onClick: async () => {
        sounds.sfx.play('ok');
        await openPopupPromise('popupCoinsShop', {});
      },
      tracking: { feature: 'header', $subFeature: 'buy-coins' },
    });

    new ImageScaleView({
      tag: 'coinFrame',
      ...uiConfig.hud.coinFrame,
      superview: layout,
    });

    this.coinsIcon = new ImageView({
      tag: 'coinIcon',
      superview: layout,
      ...uiConfig.hud.coinIcon,
    });

    new ImageView({
      tag: 'coinPlus',
      ...uiConfig.hud.coinPlus,
      superview: layout,
    });

    const coins = new LangBitmapFontTextView({
      tag: 'coinText',
      superview: layout,
      ...uiConfig.hud.coinText,
      font: bitmapFonts(uiConfig.hud.coinText.fontName),
    });

    return coins;
  }

  /**
   * Header coins icon position
   */
  public getCoinIconPos(): { x: number; y: number } {
    const position = this.coinsIcon.getPosition(this.container);
    return {
      x: position.x + this.coinsIcon.style.width / 2,
      y: getScreenTop() + position.y + this.coinsIcon.style.height / 2,
    };
  }

  /**
   * Header energy icon position
   */
  public getEnergyPos(): { x: number; y: number } {
    const position = this.energyIcon.getPosition(this.container);
    return {
      x: position.x + this.energyIcon.style.width / 2,
      y: getScreenTop() + position.y + this.energyIcon.style.height / 2,
    };
  }

  // =======================================================
  // Gems
  // =======================================================

  private createGems() {
    const layout = new ButtonScaleView({
      superview: this.container,
      ...uiConfig.hud.gemLayout,
      onClick: async () => {
        sounds.sfx.play('ok');
        // If IAP enabled just show gems shop or alternative way to get gems if IAP disabled
        await openGemsShopSequence('gem_plus_button');
      },
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    new ImageScaleView({
      ...uiConfig.hud.gemFrame,
      superview: layout,
    });

    this.gemsIcon = new ImageScaleView({
      ...uiConfig.hud.gemIcon,
      superview: layout,
    });

    this.gems = new LangBitmapFontTextView({
      superview: layout,
      ...uiConfig.hud.gemText,
      font: bitmapFonts(uiConfig.hud.gemText.fontName),
    });

    const buttonGemPlus = new ImageScaleView({
      ...uiConfig.hud.gemPlus,
      superview: layout,
    });

    // Disable or enable gems button
    createPersistentEmitter(() => isGemsButtonDisabled()).addListener(
      (gemsButtonLocked) => {
        buttonGemPlus.updateOpts({ visible: !gemsButtonLocked });
        layout.setDisabled(gemsButtonLocked);
      },
    );
  }

  getGemsIconPos(): { x: number; y: number } {
    const position = this.gemsIcon.getPosition(this.container);
    return {
      x: position.x + this.gemsIcon.style.width / 2,
      y: getScreenTop() + position.y + this.gemsIcon.style.height / 2,
    };
  }

  // =======================================================
  // Energy
  // =======================================================

  private createEnergy() {
    const layout = new ButtonScaleView({
      tag: 'energyLayout',
      superview: this.container,
      ...uiConfig.hud.energyLayout,
      onClick: async () => {
        sounds.sfx.play('ok');
        await openPopupPromise('popupEnergyShop', {});
      },
      tracking: { feature: 'header', $subFeature: 'buy-energy' },
      visible: false,
    });

    new ImageScaleView({
      tag: 'energyFrame',
      ...uiConfig.hud.energyFrame,
      superview: layout,
    });

    this.energyIcon = new ImageView({
      tag: 'energyIcon',
      superview: layout,
      ...uiConfig.hud.energyIcon,
    });

    new ImageView({
      tag: 'energyPlus',
      ...uiConfig.hud.energyPlus,
      superview: layout,
    });

    const energy = new LangBitmapFontTextView({
      tag: 'energyText',
      superview: layout,
      ...uiConfig.hud.energyText,
      font: bitmapFonts(uiConfig.hud.energyText.fontName),
    });

    this.energyRegenerationFrame = new ImageScaleView({
      superview: layout,
      ...uiConfig.hud.energyRegenerationFrame,
      visible: false,
    });

    new ImageScaleView({
      superview: this.energyRegenerationFrame,
      ...uiConfig.hud.energyRegenerationTip,
    });

    this.energyRegeneration = new LangBitmapFontTextView({
      superview: this.energyRegenerationFrame,
      ...uiConfig.hud.energyRegenerationText,
      font: bitmapFonts(uiConfig.hud.coinText.fontName),
    });

    statePromise(({ user }: State) =>
      Minigame.getters(user).areMinigamesAvailable(),
    ).then(() => {
      layout.updateOpts({ visible: true });
      this.updateEnergyTimer();
    });

    return energy;
  }

  private updateEnergyTimer() {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();

    const getter = Inventory.getters(user);
    const energy = getter.getEnergy(now);

    const { time } = getter.getEnergyRegenerationConfig();
    const timeSinceLastEnergy =
      (now - user.inventory.energyRechargeStartTime) % time;

    const timeToNextEnergy = time - timeSinceLastEnergy;

    this.energyRegenerationFrame.updateOpts({
      visible: energy < Inventory.ruleset.maxEnergy,
    });
    this.energyRegeneration.updateOpts({
      localeText: () =>
        i18n('header.energyRegeneration', {
          time: formatDuration(timeToNextEnergy < 0 ? 0 : timeToNextEnergy),
        }),
    });

    this.energyAnimator.setTarget(energy);

    createWaiter().wait(
      this.updateEnergyTimer.bind(this),
      duration({ seconds: 1 }),
    );
  }

  // =======================================================
  // Love
  // =======================================================

  private createLoveMeter() {
    const config: HudMeterOpts = {
      ...uiConfig.hud.loveMeter,
      label: uiConfig.hud.meterLabel,
      text: uiConfig.hud.meterText,
    };
    config.view.superview = this.container;
    this.loveMeter = new HudMeter(config);
  }

  // =======================================================
  // Stomach
  // =======================================================

  private createStomachMeter() {
    const config: HudMeterOpts = {
      ...uiConfig.hud.stomachMeter,
      label: uiConfig.hud.meterLabel,
      text: uiConfig.hud.meterText,
    };
    config.view.superview = this.container;
    this.stomachMeter = new HudMeter(config);
  }

  // =======================================================
  // Name Tag
  // =======================================================

  private createNameTag() {
    const {
      nameTagBackground,

      levelLabel,
      nameTagPetName,
      nameTagSpecies,
      nameTagAge,
    } = uiConfig.hud;

    const nameTagView = this.container.addSubview(
      new ImageCoverView(nameTagBackground),
    );

    this.levelProgress = new CircularProgressBar({
      superview: nameTagView,
      frameStyle: {
        x: 101 / 2,
        y: 168 / 2 + 14,
        centerOnOrigin: true,
      },
      progress: 0.7,
    });

    this.levelLabel = new LangBitmapFontTextView({
      ...levelLabel,
      superview: this.levelProgress,
    });

    this.petName = new LangBitmapFontTextView({
      ...nameTagPetName,
      superview: nameTagView,
    });

    this.petSpecies = new LangBitmapFontTextView({
      ...nameTagSpecies,
      superview: nameTagView,
    });

    this.petAge = new LangBitmapFontTextView({
      ...nameTagAge,
      superview: nameTagView,
    });
  }

  // =======================================================
  // Switch button
  // =======================================================
  private createSwitchButton() {
    this.switchButton = new ButtonScaleView({
      tag: 'switchDisplay',
      superview: this.container,
      ...uiConfig.hud.switchLayout,
      tracking: { feature: 'header', $subFeature: 'buy-energy' },
      visible: false,
    });

    new ImageScaleView({
      superview: this.switchButton,
      ...uiConfig.hud.switchFrame,
    });

    new LangBitmapFontTextView({
      superview: this.switchButton,
      ...uiConfig.hud.switchText,
      localeText: () => i18n('friendsVisit.switch'),
    });
  }

  // =======================================================
  // Friend's name plate
  // =======================================================

  private createFriendsRoomPlate() {
    this.friendsNamePlate = new ImageScaleView({
      superview: this.container,
      image: 'assets/ui/visit/frames/friend_plate.png',
      x: this.container.style.width * 0.5,
      y: 240,
      width: 338,
      height: 150,
      centerOnOrigin: true,
    });

    this.friendsNamePlateText = new LangBitmapFontTextView({
      superview: this.friendsNamePlate,
      x: this.friendsNamePlate.style.width * 0.5,
      y: 100,
      width: 307,
      height: 47,
      align: 'center',
      verticalAlign: 'center',
      size: 22,
      color: '#fff',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () =>
        i18n('friendsVisit.livingRoom.plate', { friend: 'Tama' }),
      centerOnOrigin: true,
    });
  }
}
